import { removeAttributeRecursive } from "./DOM";
import { inlineCSSRules, cssHslToRGB } from "./CSS";

export function svgToDataURL(svg: SVGSVGElement | null) {
  if (!svg) return null;

  const svgElement = svg.cloneNode(true) as SVGSVGElement;

  // add dependencies in SVG defs block
  const deps = collectSVGDeps(svgElement);

  let defsElement = svgElement.querySelector("defs");
  if (!defsElement) {
    defsElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "defs"
    );
    svgElement.appendChild(defsElement);
  }

  for (const dep of deps) {
    const newDep = dep.cloneNode(true) as SVGElement;

    // remove any fill attribute, as those will interfere with
    // fill properties on the dependencies in other software.
    removeAttributeRecursive(newDep, "fill");

    defsElement.appendChild(newDep);
  }

  // add the new element to the source SVG parent, to evaluate
  // all of the CSS in the correct context.
  const parentElement = svg.parentElement;
  parentElement?.appendChild(svgElement);

  inlineCSSRules(svgElement);

  cleanAttributesRecursive(svgElement);

  // remove all hidden elements
  svgElement
    .querySelectorAll('[style*="display: none"]')
    .forEach((element) => element.remove());

  parentElement?.removeChild(svgElement);

  const svgString = new XMLSerializer().serializeToString(svgElement);

  // Remove any characters outside the Latin1 range
  const decoded = unescape(encodeURIComponent(svgString));

  // Now we can use btoa to convert the svg to base64
  const base64 = btoa(decoded);

  const imgSource = `data:image/svg+xml;base64,${base64}`;

  return imgSource;
}

export function cleanAttributesRecursive(element: Element) {
  for (const attr of Array.from(element.attributes)) {
    // move hsl colors to rgb
    if (attr.value.startsWith("hsl(")) {
      element.setAttribute(attr.name, cssHslToRGB(attr.value));
    }

    // replace double quotes with single quotes
    if (attr.value.includes('"')) {
      element.setAttribute(attr.name, attr.value.replaceAll('"', "'"));
    }
  }

  for (const child of Array.from(element.children)) {
    cleanAttributesRecursive(child);
  }
}

export function collectSVGDeps(
  element: SVGElement,
  deps: Set<SVGElement> = new Set()
) {
  searchAndAddDependency(element, "clip-path", deps);
  searchAndAddDependency(element, "src", deps);
  searchAndAddDependency(element, "href", deps);

  for (const child of Array.from(element.children)) {
    if (child instanceof SVGElement) {
      collectSVGDeps(child, deps);
    }
  }

  return deps;
}

function searchAndAddDependency(
  element: SVGElement,
  attribute: string,
  deps: Set<SVGElement>
) {
  if (!element.hasAttribute(attribute)) return;
  const selector = getReferenceId(element.getAttribute(attribute));
  if (selector) {
    const referencedElement = document.querySelector(selector) as SVGElement;

    // only add dependencies that are not part of the object
    if (element.ownerSVGElement != referencedElement.ownerSVGElement) {
      // do not add duplicates
      deps.add(referencedElement);
    }
  }
}

/**
 * Extract an id reference from an attribute.
 * @param attributeValue The value of an attribute: e.g. "url(#symbol-circle)"
 * @returns The id reference as string: e.g. "#symbol-circle"
 */
function getReferenceId(attributeValue: string | null) {
  const attribute = attributeValue;
  if (!attribute) return null;

  const idPatternIndex = attribute.indexOf("#") ?? -1;
  if (attribute && idPatternIndex > -1) {
    const endIndex = attribute.indexOf(")", idPatternIndex);
    const selector = attribute.substring(
      idPatternIndex,
      endIndex >= idPatternIndex ? endIndex : undefined
    );
    return selector;
  }
  return null;
}
